class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.sectionsStorageKey = 'santo-cart-drawer-sections';
    this.handleOverlayClick = this.close.bind(this);
    this.handlePageShow = this.onPageShow.bind(this);
    this.handleRebuyCartChange = this.onRebuyCartChange.bind(this);
    this.handleHeaderCartIconClick = this.onHeaderCartIconClick.bind(this);
    this.handleHeaderCartIconKey = this.onHeaderCartIconKey.bind(this);
    this.lastCartFingerprint = null;
    this.fingerprintReady = this.captureCartFingerprintFromCart();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlayClick();
    this.bindHeaderCartIcon();
    this.setHeaderCartIconAccessibility();
  }

  computeFingerprint(cart) {
    return [
      cart.token,
      cart.item_count,
      cart.total_price,
      (cart.cart_level_discount_applications || []).map((d) => d.title).join('|'),
    ].join(':');
  }

  async captureCartFingerprintFromCart() {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      this.lastCartFingerprint = this.computeFingerprint(cart);
    } catch (err) {}
  }

  async onRebuyCartChange() {
    try {
      // Wait for the initial baseline so Rebuy's own init dispatch — which
      // races our constructor fetch — doesn't trigger a useless refresh.
      // Also covers the re-baseline started by renderContents/refreshContents,
      // so a Rebuy event that arrives mid-render compares against what was
      // actually drawn rather than the pre-render fingerprint.
      if (this.fingerprintReady) await this.fingerprintReady;
      // No time-based suppression here. A 750ms window measured from the last
      // cartUpdate silently dropped genuine changes: an add Rebuy made itself
      // (which publishes no cartUpdate of ours) landing inside that window
      // never rendered, and nothing re-fetched afterwards, so the drawer stayed
      // stale until the next unrelated event. The fingerprint below already
      // answers the only question worth asking — did the cart actually change
      // since we last drew it — and it answers it correctly at any timing.
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const fingerprint = this.computeFingerprint(cart);
      if (fingerprint === this.lastCartFingerprint) return;
      // The cart differs from our baseline, but a theme-driven update may still
      // be in flight: updateQuantity publishes cartUpdate (which re-baselines
      // from the same cart) at the END of its handler, and Rebuy's echo can beat
      // it. Refreshing here would repaint what the theme is already drawing.
      // Yield, then re-check the baseline: if the theme claimed this state in
      // the meantime, it is drawing it and there is nothing for us to do. A
      // genuine out-of-band change — a rec-tile add, which publishes no
      // cartUpdate — still fails the re-check and renders.
      await new Promise((resolve) => setTimeout(resolve, 300));
      if (fingerprint === this.lastCartFingerprint) return;
      this.lastCartFingerprint = fingerprint;
      this.refreshContents();
    } catch (err) {
      this.refreshContents();
    }
  }

  connectedCallback() {
    window.addEventListener('pageshow', this.handlePageShow);
    document.addEventListener('rebuy:cart.change', this.handleRebuyCartChange);
    // Stamp lastRenderedAt on every theme-internal cart update (still read by
    // CartDrawerItems.onCartUpdate) and, more importantly, re-baseline the cart
    // fingerprint from the payload.
    //
    // CartItems.updateQuantity — the quantity and REMOVE path — renders the
    // drawer itself rather than going through renderContents, so nothing else
    // re-baselines. Left stale, the fingerprint still describes the cart from
    // BEFORE the change, so the Rebuy echo that follows every cart mutation
    // compares it against the new state, sees a difference, and fires a second
    // full render: the empty cart's category grid tears down and repaints, and
    // its images are re-fetched. That is the flash on removing the last item.
    //
    // The publish carries the new cart, so re-baseline straight from it — no
    // fetch, no timer, no window to race.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.lastRenderedAt = Date.now();
        const cart = event && event.cartData;
        // Guard on cart shape: product-form publishes the /cart/add.js response,
        // which describes the added line, not the cart. Those go through
        // renderContents, which re-baselines with its own fetch.
        if (cart && typeof cart.item_count === 'number' && typeof cart.total_price === 'number') {
          this.lastCartFingerprint = this.computeFingerprint(cart);
        }
      });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.handlePageShow);
    document.removeEventListener('rebuy:cart.change', this.handleRebuyCartChange);
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    this.unbindHeaderCartIcon();
    this.unbindOverlayClick();
  }

  bindOverlayClick() {
    this.unbindOverlayClick();
    this.overlayElement = this.querySelector('#CartDrawer-Overlay');
    if (this.overlayElement) {
      this.overlayElement.addEventListener('click', this.handleOverlayClick);
    }
  }

  unbindOverlayClick() {
    if (this.overlayElement) {
      this.overlayElement.removeEventListener('click', this.handleOverlayClick);
    }
  }

  bindHeaderCartIcon() {
    // Delegate at document level so the click handler survives even if a
    // third-party script (Klaviyo, etc.) re-renders the cart icon and
    // strips direct listeners. capture=true ensures we run before any
    // injected handlers that might stopPropagation.
    document.addEventListener('click', this.handleHeaderCartIconClick, true);
    document.addEventListener('keydown', this.handleHeaderCartIconKey, true);
  }

  unbindHeaderCartIcon() {
    document.removeEventListener('click', this.handleHeaderCartIconClick, true);
    document.removeEventListener('keydown', this.handleHeaderCartIconKey, true);
  }

  onHeaderCartIconClick(event) {
    const cartLink = event.target.closest('#cart-icon-bubble');
    if (!cartLink) return;
    event.preventDefault();
    this.open(cartLink);
  }

  onHeaderCartIconKey(event) {
    if (event.code.toUpperCase() !== 'SPACE') return;
    const cartLink = event.target.closest('#cart-icon-bubble');
    if (!cartLink) return;
    event.preventDefault();
    this.open(cartLink);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
  }

  onPageShow(event) {
    const navigationEntry =
      typeof performance.getEntriesByType === 'function'
        ? performance.getEntriesByType('navigation')[0]
        : null;
    const isBackForwardNavigation =
      event.persisted || navigationEntry?.type === 'back_forward';
    if (!isBackForwardNavigation) return;
    this.hydrateFromStoredSections();
    this.refreshContents();
  }

  readStoredSections() {
    try {
      const storedValue = window.sessionStorage.getItem(this.sectionsStorageKey);
      if (!storedValue) return null;
      return JSON.parse(storedValue);
    } catch (error) {
      return null;
    }
  }

  writeStoredSections(sections) {
    if (!sections) return;
    try {
      window.sessionStorage.setItem(
        this.sectionsStorageKey,
        JSON.stringify(sections),
      );
    } catch (error) {}
  }

  hydrateFromStoredSections() {
    const storedSections = this.readStoredSections();
    if (!storedSections) return;
    this.applySections(storedSections);
  }

  applySections(sections) {
    // Toggle is-empty BEFORE replacing innerHTML so the new
    // cart-drawer-recommendations element sees the correct cart state
    // in its connectedCallback — it bails out hidden if the drawer is
    // empty.
    const drawerSectionMarkup = sections['cart-drawer'];
    if (drawerSectionMarkup) {
      const drawerRoot = this.getSectionDOM(drawerSectionMarkup, 'cart-drawer');
      this.classList.toggle(
        'is-empty',
        Boolean(drawerRoot?.classList.contains('is-empty')),
      );
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionMarkup = sections[section.id];
      if (!sectionMarkup) return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        sectionMarkup,
        section.selector,
      );
    });

    this.bindOverlayClick();
    this.setHeaderCartIconAccessibility();
  }

  refreshContents() {
    const sectionIds = this.getSectionsToRender().map((section) => section.id);
    const requestUrl = new URL(window.location.href);
    requestUrl.searchParams.set('sections', sectionIds.join(','));

    fetch(requestUrl.toString(), { cache: 'no-store' })
      .then((response) => response.json())
      .then(async (sections) => {
        this.writeStoredSections(sections);
        this.applySections(sections);
        // Same re-baseline contract as renderContents: publish the promise so a
        // Rebuy event landing mid-refresh waits for the new fingerprint instead
        // of comparing against the stale one and drawing a second time.
        this.fingerprintReady = (async () => {
          try {
            const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
            this.lastCartFingerprint = this.computeFingerprint(cart);
          } catch (err) {}
        })();
        await this.fingerprintReady;
      })
      .catch((error) => {
        console.error(error);
      });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // Lets the empty-cart category images load (see component-cart-drawer.css).
    this.classList.add('has-opened');
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.dispatchEvent(new CustomEvent('cart-drawer:close', { bubbles: true }));
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.writeStoredSections(parsedState.sections);
    this.applySections(parsedState.sections);
    // Tell other handlers (CartDrawerItems.onCartUpdate,
    // onRebuyCartChange) we just rendered fresh sections so they don't
    // re-fetch and re-render the same data — the redundant render was
    // visible as a flicker on iOS.
    this.lastRenderedAt = Date.now();
    // Re-baseline the fingerprint asynchronously so subsequent Rebuy
    // events compare against the actually-rendered cart state. Held on
    // fingerprintReady so onRebuyCartChange awaits it instead of guessing
    // with a timer: an event arriving mid-render now waits for the new
    // baseline and correctly compares equal, which is what kept the
    // duplicate render (the iOS flicker) away.
    this.fingerprintReady = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        this.lastCartFingerprint = this.computeFingerprint(cart);
      })
      .catch(() => {});

    setTimeout(() => {
      this.bindOverlayClick();
      this.setHeaderCartIconAccessibility();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        selector: '#cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        // The section renders the whole <a id="cart-icon-bubble">, so targeting
        // '.shopify-section' would write that entire anchor into the header's
        // own anchor — a duplicate-id <a role="button"> nested inside another.
        // Target the anchor itself so only its contents are swapped, matching
        // CartDrawer.getSectionsToRender.
        selector: '#cart-icon-bubble',
      },
    ];
  }

  onCartUpdate() {
    // The publishers of PUB_SUB cartUpdate (product-form, quick-add,
    // quick-order-list) call cart-drawer.renderContents synchronously
    // after publish() returns. The parent CartItems.onCartUpdate fires
    // inside publish() and re-fetches the same data — a visible flicker
    // on iOS. Defer to a microtask so the synchronous renderContents
    // call has a chance to stamp lastRenderedAt first. Use document
    // lookup (not this.closest) because renderContents detaches the
    // old cart-drawer-items element before the microtask runs.
    queueMicrotask(() => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.lastRenderedAt && Date.now() - drawer.lastRenderedAt < 750) {
        return;
      }
      super.onCartUpdate();
    });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);